import {
  BaseBotFormValues,
  RestoreChangesType,
  RestoreTabChangesData,
  Steps,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useCompareBotValues } from "@shared/features/bots/BotForm/common/hooks";

interface useBotsRestoreChangesProps {
  setFormValues: (
    baseFormValues?: BaseBotFormValues,
    keysToSet?: (keyof BaseBotFormValues)[],
    keysToIgnore?: (keyof BaseBotFormValues)[]
  ) => void;
  originalBotValues?: BaseBotFormValues | undefined;
  originalCheckedFields?: Set<ToggleField> | undefined;
  templateValues?: BaseBotFormValues | undefined;
  templateCheckedFields?: Set<ToggleField> | undefined;
}

const useBotsRestoreChanges = ({
  setFormValues,
  originalBotValues,
  originalCheckedFields,
  templateValues,
  templateCheckedFields,
}: useBotsRestoreChangesProps) => {
  const { toggleField, addFieldToInitialize } = useContext(BotFormContext)!;
  const { compareValues } = useCompareBotValues();

  const resetTab = (tab: Steps, restoreType?: RestoreChangesType) => {
    const restoreToOriginalBot =
      restoreType === RestoreChangesType.ORIGINAL_BOT;
    const restoreValues = restoreToOriginalBot
      ? originalBotValues
      : templateValues;
    const restoreCheckedFields = restoreToOriginalBot
      ? originalCheckedFields
      : templateCheckedFields;
    const keysToReset = TAB_TO_RESET_DATA[tab];
    if (keysToReset) {
      setFormValues(restoreValues, keysToReset);
      if (tab === Steps.ENTRIES) {
        addFieldToInitialize?.("entryStrategy");
      } else if (tab === Steps.TAKE_PROFITS) {
        addFieldToInitialize?.("takeProfitStrategy");
      } else if (tab === Steps.GENERAL) {
        addFieldToInitialize?.("amountValue");
      }
    }
    const toggleFields = TAB_TO_TOGGLE_FIELDS[tab];
    if (toggleFields) {
      toggleFields.forEach((field) => {
        toggleField(field, !!restoreCheckedFields?.has(field));
      });
    }
  };

  const getResetBulkEditTabData = (
    tab: Steps
  ): RestoreTabChangesData | undefined => {
    const keysToReset = TAB_TO_RESET_DATA[tab];
    if (keysToReset) {
      const changedFromTemplate =
        !!templateValues &&
        compareValues(
          keysToReset,
          templateValues,
          templateCheckedFields,
          undefined
        );
      const changedFromOriginalBot =
        !!originalBotValues &&
        compareValues(
          keysToReset,
          originalBotValues,
          originalCheckedFields,
          undefined
        );
      let restoreChangesTypes: RestoreChangesType[] = [];
      if (changedFromOriginalBot)
        restoreChangesTypes.push(RestoreChangesType.ORIGINAL_BOT);
      if (changedFromTemplate)
        restoreChangesTypes.push(RestoreChangesType.TEMPLATE);
      const valuesChanged = changedFromTemplate || changedFromOriginalBot;
      return {
        valuesChanged,
        restoreChangesTypes,
        resetTab: (restoreType?: RestoreChangesType) =>
          resetTab(tab, restoreType),
      };
    }
  };

  const tabToRestoreChangesData = {
    [Steps.STOP]: getResetBulkEditTabData(Steps.STOP),
    [Steps.ENTRIES]: getResetBulkEditTabData(Steps.ENTRIES),
    [Steps.TAKE_PROFITS]: getResetBulkEditTabData(Steps.TAKE_PROFITS),
    [Steps.GENERAL]: getResetBulkEditTabData(Steps.GENERAL),
    [Steps.ADVANCED]: getResetBulkEditTabData(Steps.ADVANCED),
  };

  return {
    tabToRestoreChangesData,
  };
};

export default useBotsRestoreChanges;

const TAB_TO_RESET_DATA: {
  [key in Steps]?: (keyof BaseBotFormValues)[];
} = {
  [Steps.ENTRIES]: [
    "entryStrategy",
    "trailingEntry",
    "entryGracePercent",
    "entryGraceBaseline",
    "entryGraceTargetExchange",
    "longHedgingRatio",
    "hedgingMode",
    "hedgingGridsData",
  ],
  [Steps.STOP]: [
    "defaultStop",
    "stopTimeout",
    "trailingStopData",
    "numberOfContinuousStopsTillBotStop",
  ],
  [Steps.ADVANCED]: [
    "followFuturesAsSpot",
    "advancedSettings",
    "stopType",
    "stopLimitPriceReduction",
    "operationHoursEvents",
  ],
  [Steps.TAKE_PROFITS]: [
    "takeProfitStrategy",
    "trailingTakeProfit",
    "trailingTakeProfitLastTargetOnly",
    "defaultTakeProfit",
    "takeProfitGracePercent",
    "takeProfitGraceTargetExchange",
  ],
  [Steps.GENERAL]: [
    "amountType",
    "amountValue",
    "longShort",
    "longHedgingRatio",
    "hedgingMode",
    "hedgingGridsData",
    "marginType",
    "multiplier",
    "exchanges",
  ],
};
const TAB_TO_TOGGLE_FIELDS: {
  [key in Steps]?: ToggleField[];
} = {
  [Steps.ENTRIES]: [
    ToggleField.ENTRIES,
    ToggleField.FIRST_ENTRY,
    ToggleField.TRAILING_ENTRY,
    ToggleField.ENTRY_GRACE_PERCENT,
  ],
  [Steps.STOP]: [
    ToggleField.STOP,
    ToggleField.STOP_TIMEOUT,
    ToggleField.TRAILING_STOP,
  ],
  [Steps.TAKE_PROFITS]: [
    ToggleField.TAKE_PROFITS,
    ToggleField.TAKE_PROFIT_STRATEGY,
    ToggleField.TRAILING_TAKE_PROFIT,
    ToggleField.TAKE_PROFIT_GRACE_PERCENT,
  ],
  [Steps.GENERAL]: [ToggleField.AMOUNT_PER_TRADE],
};
